import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { inr } from "@/lib/api";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, Legend } from "recharts";
import WhatsAppButton from "@/components/WhatsAppButton";
import { Wallet } from "lucide-react";

const COLORS = ["#C45A41","#3D5A80","#D99036","#4A7258","#B83A3A","#7A5117","#2C4763","#5C8F6D"];

export default function Reports() {
  const [d, setD] = useState(null);
  const [best, setBest] = useState([]);
  const [clv, setClv] = useState([]);
  const [byCat, setByCat] = useState([]);
  const [payables, setPayables] = useState([]);
  useEffect(() => {
    api.get("/reports/summary").then(r => setD(r.data));
    api.get("/analytics/best-sellers").then(r => setBest(r.data));
    api.get("/analytics/clv").then(r => setClv(r.data));
    api.get("/analytics/by-category").then(r => setByCat(r.data));
    api.get("/vendors-payables-summary").then(r => setPayables(r.data));
  }, []);
  if (!d) return <div className="p-8 text-[#757169]">Loading…</div>;
  const stagePie = Object.entries(d.stage_counts).map(([k,v]) => ({ name: k, value: v }));
  const totalOutstanding = payables.reduce((a,b)=>a+Number(b.outstanding||0), 0);
  const totalBilledV = payables.reduce((a,b)=>a+Number(b.billed||0), 0);
  const totalPaidV = payables.reduce((a,b)=>a+Number(b.paid||0), 0);

  return (
    <div className="space-y-6">
      <div>
        <div className="label-tiny mb-1">Admin</div>
        <h1 className="font-display text-3xl tracking-tight">Reports</h1>
        <p className="text-sm text-[#757169] mt-1">Real profit — actual revenue minus actual expenses. Never estimates.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Revenue collected" value={inr(d.total_revenue)} />
        <Kpi label="Billed" value={inr(d.total_billed)} />
        <Kpi label="Expenses" value={inr(d.total_expenses)} />
        <Kpi label="Profit" value={inr(d.profit)} tone={d.profit >= 0 ? "text-[#4A7258]" : "text-[#B83A3A]"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="card-surface p-5">
          <div className="label-tiny mb-3">Monthly revenue</div>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={d.monthly_revenue}>
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v)=>inr(v)} />
                <Bar dataKey="revenue" fill="#C45A41" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-surface p-5">
          <div className="label-tiny mb-3">Order status</div>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={stagePie} dataKey="value" nameKey="name" outerRadius={80} label>
                  {stagePie.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Category analytics */}
      <div className="card-surface p-5" data-testid="category-analytics">
        <div className="label-tiny mb-3">Revenue by product category</div>
        {byCat.length === 0 ? (
          <div className="text-sm text-[#757169]">No orders yet. Tag products with a category to see this breakdown.</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <BarChart data={byCat} layout="vertical" margin={{ left: 30 }}>
                  <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => `₹${v/1000}k`} />
                  <YAxis type="category" dataKey="category" tick={{ fontSize: 11 }} width={110} />
                  <Tooltip formatter={(v)=>inr(v)} />
                  <Bar dataKey="revenue" fill="#3D5A80" radius={[0,4,4,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead><tr><th>Category</th><th>Qty</th><th>Orders</th><th>Revenue</th></tr></thead>
                <tbody>
                  {byCat.map(c => (
                    <tr key={c.category} data-testid={`cat-${c.category}`}>
                      <td className="font-medium">{c.category}</td>
                      <td className="mono">{c.qty}</td>
                      <td className="mono">{c.orders}</td>
                      <td className="mono">{inr(c.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Vendor payables ledger */}
      <div className="card-surface p-5" data-testid="vendor-payables-summary">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="flex items-center gap-2">
            <Wallet size={16} className="text-[#C45A41]"/>
            <div className="label-tiny">Vendor payables — who we owe</div>
          </div>
          <div className="text-sm text-[#75706A]">
            Billed <span className="mono text-[#2C2B29] font-bold">{inr(totalBilledV)}</span> ·
            Paid <span className="mono text-[#4A7258] font-bold">{inr(totalPaidV)}</span> ·
            Outstanding <span className="mono text-[#B83A3A] font-bold">{inr(totalOutstanding)}</span>
          </div>
        </div>
        {payables.length === 0 ? (
          <div className="text-sm text-[#757169]">No vendor bills yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Vendor</th><th>Billed</th><th>Paid</th><th>Outstanding</th><th>Actions</th></tr></thead>
              <tbody>
                {payables.map(p => {
                  const msg = `Hi ${p.vendor_name}, this is Marsh Clothing. Our books show an outstanding balance of ₹${Number(p.outstanding).toLocaleString("en-IN")} against ₹${Number(p.billed).toLocaleString("en-IN")} billed. Could you please confirm settlement / share invoice at your convenience? Thanks 🙏`;
                  return (
                    <tr key={p.vendor_id} data-testid={`payable-row-${p.vendor_id}`}>
                      <td><Link to={`/vendors/${p.vendor_id}`} className="font-medium hover:underline">{p.vendor_name}</Link></td>
                      <td className="mono">{inr(p.billed)}</td>
                      <td className="mono text-[#4A7258]">{inr(p.paid)}</td>
                      <td className={`mono font-bold ${p.outstanding > 0 ? "text-[#B83A3A]" : "text-[#4A7258]"}`}>{inr(p.outstanding)}</td>
                      <td>
                        <div className="flex flex-wrap gap-1.5">
                          {p.phone && p.outstanding > 0 && (
                            <WhatsAppButton phone={p.phone} message={msg} label="Remind" size="sm" testid={`payable-remind-${p.vendor_id}`}/>
                          )}
                          <Link to={`/vendors/${p.vendor_id}`} className="btn-ghost text-xs">Open →</Link>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="card-surface p-5">
          <div className="label-tiny mb-3">Best sellers (by velocity)</div>
          {best.length === 0 ? <div className="text-sm text-[#757169]">Not enough data yet.</div> :
            <table className="data-table">
              <thead><tr><th>Product</th><th>Qty sold</th><th>Orders</th><th>Revenue</th></tr></thead>
              <tbody>
                {best.map(p => (
                  <tr key={p.product} data-testid={`best-${p.product}`}>
                    <td className="font-medium">{p.product}</td>
                    <td className="mono">{p.qty}</td>
                    <td className="mono">{p.orders}</td>
                    <td className="mono">{inr(p.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>}
        </div>

        <div className="card-surface p-5">
          <div className="label-tiny mb-3">Customer Lifetime Value (VIPs)</div>
          {clv.length === 0 ? <div className="text-sm text-[#757169]">Not enough data yet.</div> :
            <table className="data-table">
              <thead><tr><th>Customer</th><th>Orders</th><th>Billed</th><th>Paid</th></tr></thead>
              <tbody>
                {clv.slice(0,10).map(c => (
                  <tr key={c.customer_name}>
                    <td className="font-medium">{c.customer_name}</td>
                    <td className="mono">{c.orders}</td>
                    <td className="mono">{inr(c.revenue)}</td>
                    <td className="mono">{inr(c.paid)}</td>
                  </tr>
                ))}
              </tbody>
            </table>}
        </div>
      </div>
    </div>
  );
}

const Kpi = ({ label, value, tone }) => (
  <div className="card-surface p-4">
    <div className="label-tiny">{label}</div>
    <div className={`font-display text-2xl font-bold mt-1 ${tone||""}`}>{value}</div>
  </div>
);
